import { StyleSheet, type ViewStyle } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export const RESULT_AVATAR_SIZE = 26;
export const RESULT_COIN_SIZE = 11;

/** The opponent ring every outcome shares; the colour is the variant. */
const avatarBase = (theme: Theme): ViewStyle => ({
  borderWidth: 2,
  backgroundColor: theme.background.inset,
});

export const getLastResultsCardStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.background.card,
      borderRadius: 14,
      paddingVertical: 10,
      paddingHorizontal: 12,
      gap: 8,
      shadowColor: theme.shadow,
      shadowOpacity: 0.4,
      shadowRadius: 4,
      shadowOffset: { width: 0, height: 2 },
      elevation: 2,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 8,
    },
    headerText: {
      flex: 1,
      minWidth: 0,
      gap: 1,
    },
    label: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.gray[500],
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    subtitle: {
      fontSize: 10,
      color: theme.gray[500],
    },
    cta: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.primary,
    },
    ctaPressed: {
      opacity: 0.7,
    },
    row: {
      flexDirection: 'row',
      gap: 8,
    },
    chip: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      paddingVertical: 6,
      paddingHorizontal: 8,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.background.inset,
    },
    chipPressed: {
      opacity: 0.75,
    },
    chipHover: {
      opacity: 0.9,
    },
    // FriendAvatar takes one ViewStyle, so each ring variant is a complete style (no runtime merging).
    avatarWon: {
      ...avatarBase(theme),
      borderColor: theme.success,
    },
    avatarLost: {
      ...avatarBase(theme),
      borderColor: theme.error,
    },
    avatarDraw: {
      ...avatarBase(theme),
      borderColor: theme.gray[200],
    },
    chipBody: {
      flex: 1,
      minWidth: 0,
    },
    name: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.white,
    },
    outcome: {
      fontSize: 10,
      color: theme.gray[500],
      fontVariant: ['tabular-nums'],
    },
    delta: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
    deltaText: {
      fontSize: 12,
      fontWeight: '800',
      fontVariant: ['tabular-nums'],
    },
    deltaWon: {
      color: theme.success,
    },
    deltaLost: {
      color: theme.error,
    },
    coin: {
      width: RESULT_COIN_SIZE,
      height: RESULT_COIN_SIZE,
    },
    empty: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      paddingVertical: 4,
    },
    emptyPressed: {
      opacity: 0.75,
    },
    emptyText: {
      flex: 1,
      minWidth: 0,
      fontSize: 12,
      color: theme.gray[500],
    },
    emptyCta: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.primary,
    },
  });

export type LastResultsCardStyles = ReturnType<typeof getLastResultsCardStyles>;
